/* global I18n */

import {Controller} from "@hotwired/stimulus"
import {useIntersection} from "stimulus-use"
import {createRoot} from "react-dom/client"
import EmbeddedVideo from "../components/embedded_video"
import _ from "lodash"
import vimeoManager from "../vimeo_manager"
import viewportHelper from "../viewport_helper"
import Contentbox from "../components/contentbox"
import ajax from "../ajax"
import React from "react"

class EmbeddedVideoController extends Controller {
  static values = {
    id: Number,
    autoPlay: {type: Boolean, default: false},
    showContentTabs: {type: Boolean, default: true}
  }

  connect() {
    const [, unobserve] = useIntersection(this, {
      rootMargin: "-80px 0px 300px 0px"
    })
    this.unobserve = unobserve
    this.abortController = new AbortController()
  }

  disconnect() {
    this.abortController.abort("Embedded video disconnected")
    if (this.root) this.root.unmount()
    this.root = null
  }

  async appear() {
    this.unobserve()
    this.video = await getVideoData(
      this.idValue, this.abortController.signal
    )
    this._updateVideoTitleForArticle()
    this.element.classList.remove("spinner")
    this.root = this.root || createRoot(this.element)
    if (this.element.classList.contains("contentbox-container")) {
      this._createVideoContentbox(this.video)
    } else {
      this._createEmbeddedVideo(this.video)
    }
    if (this.element.id && window.location.hash.match(this.element.id)) {
      this.element.children[0].click()
    }
  }

  _createVideoContentbox(video) {
    this.root.render(
      <Contentbox
        title={video.title}
        description={video.description}
        imageUrl={video.thumbUrl}
        onClick={this._showEmbeddedVideo.bind(this)}
        type="video"
      />
    )
  }

  _showEmbeddedVideo() {
    vimeoManager.pauseAll()
    this.root.unmount()
    this.root = createRoot(this.element)
    this._createEmbeddedVideo(this.video)
    viewportHelper.goTo(this.element)
  }

  _createEmbeddedVideo(video) {
    const startTime =
      video.canWatchFullVideo ? getStartTimeFromUrl() : 0
    this.root.render(
      <EmbeddedVideo
        title={video.title}
        htmlTitle={video.htmlTitle}
        autoPlay={this.autoPlayValue}
        playTime={video.playTime}
        language={video.language}
        description={video.description}
        transcript={video.transcript}
        timestamps={video.timestamps}
        vimeoId={video.vimeoId}
        vimeoThumbnailUrl={video.vimeoThumbnailUrl}
        canWatchFullVideo={video.canWatchFullVideo}
        restricted={video.restricted}
        trackingData={createTrackingData(video)}
        showContentTabs={this.showContentTabsValue}
        startTime={startTime}
      />
    )
  }

  _updateVideoTitleForArticle() {
    if (!this.element.closest("article")) return

    this.video.htmlTitle = (
      <a href={this.video.videoPath}>
        {I18n.t("js.embedded_video.recommended_video")}
        {this.video.title} [{this.video.playTime}]
      </a>
    )
  }
}

function createTrackingData(video) {
  const trackingData =
    window.gon.tracking?.studyUnit ||
    window.gon.tracking?.article ||
    {}
  return Object.assign(
    video.trackingData,
    _.pick(trackingData, [
      "sectionSlug",
      "lectureSlug",
      "chapterSlug"
    ])
  )
}

function getVideoData(id, abortSignal) {
  // we provide the data for the first video on articles on page load
  // if it is on top of the page
  if (window.gon.videos && window.gon.videos[id]) {
    return Promise.resolve(window.gon.videos[id])
  } else if (window.gon.video) {
    return Promise.resolve(window.gon.video)
  }
  return ajax.get(`/api/videos/${id}.json`, null, {signal: abortSignal})
}

// this is used on the video page
function getStartTimeFromUrl() {
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get("t")
}

export default EmbeddedVideoController
